import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { toast } from 'react-toastify';
import { Plus, Edit, Trash2, X, Image as ImageIcon, List } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubcategoryManager = ({ category, onClose }) => {
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [newSubcategoryName, setNewSubcategoryName] = useState('');
  const [newSubcategoryImage, setNewSubcategoryImage] = useState('');
  const [newSubcategoryOrder, setNewSubcategoryOrder] = useState(0);
  const [newSubcategoryMemberOnly, setNewSubcategoryMemberOnly] = useState(false);
  const [showImageLibrary, setShowImageLibrary] = useState(false);
  const [imageLibraryImages, setImageLibraryImages] = useState([]);

  useEffect(() => {
    fetchSubcategories();
  }, [category.id]);

  // Auto-load images when image library opens
  useEffect(() => {
    if (showImageLibrary) {
      searchImageLibrary(''); // Empty search to get all images
    }
  }, [showImageLibrary]);

  const fetchSubcategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/content-subcategories/by-category/${category.id}`);
      setSubcategories(response.data || []);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      toast.error('Error loading subcategories');
    } finally {
      setLoading(false);
    }
  };

  const createSubcategory = async (e) => {
    e.preventDefault();
    if (!newSubcategoryName.trim()) {
      toast.error('Please enter a subcategory name');
      return;
    }

    try {
      await axios.post(`${API}/admin/content-subcategories`, {
        category_id: category.id,
        name: newSubcategoryName.trim(),
        image_url: newSubcategoryImage || null,
        order: newSubcategoryOrder,
        member_only: newSubcategoryMemberOnly
      });
      toast.success('Subcategory created successfully');
      setNewSubcategoryName('');
      setNewSubcategoryImage('');
      setNewSubcategoryOrder(0);
      setNewSubcategoryMemberOnly(false);
      setShowForm(false);
      fetchSubcategories();
    } catch (error) {
      console.error('Error creating subcategory:', error);
      toast.error(error.response?.data?.detail || 'Error creating subcategory');
    }
  };

  const updateSubcategory = async (subcategoryId, updates) => {
    try {
      await axios.put(`${API}/admin/content-subcategories/${subcategoryId}`, updates);
      toast.success('Subcategory updated successfully');
      setEditingSubcategory(null);
      fetchSubcategories();
    } catch (error) {
      console.error('Error updating subcategory:', error);
      toast.error('Error updating subcategory');
    }
  };

  const deleteSubcategory = async (subcategoryId) => {
    if (!window.confirm('Are you sure you want to delete this subcategory?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/content-subcategories/${subcategoryId}`);
      toast.success('Subcategory deleted successfully');
      fetchSubcategories();
    } catch (error) {
      console.error('Error deleting subcategory:', error);
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Cannot delete subcategory with existing content');
      } else {
        toast.error('Error deleting subcategory');
      }
    }
  };

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `${API}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  const searchImageLibrary = async (search = '') => {
    try {
      const response = await axios.get(`${API}/admin/image-library`, {
        params: search ? { search, public_access: "true" } : { public_access: "true" }
      });
      
      // Transform the response to match the expected format
      const transformedImages = response.data.map(image => ({
        id: image.id,
        urls: {
          small: getImageUrl(image.url),
          regular: getImageUrl(image.url)
        },
        alt_description: image.description || image.name,
        name: image.name,
        description: image.description,
        tags: image.tags || []
      }));
      
      setImageLibraryImages(transformedImages);
    } catch (error) {
      console.error('Error loading images:', error);
      toast.error('Error loading image library');
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-cream border-burgundy">
        <CardHeader className="border-b border-burgundy/20">
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-2xl font-serif text-burgundy">
                Manage Subcategories
              </CardTitle>
              <p className="text-burgundy/70 mt-1">
                Category: <span className="font-semibold">{category.title}</span>
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-burgundy hover:bg-burgundy/10"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-6 space-y-6">
          {/* Add New Subcategory Form */}
          {!showForm ? (
            <Button
              onClick={() => setShowForm(true)}
              className="w-full bg-burgundy hover:bg-burgundy/80 text-cream"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Subcategory
            </Button>
          ) : (
            <form onSubmit={createSubcategory} className="space-y-4 p-4 border border-burgundy/20 rounded-lg bg-white">
              <div>
                <label className="block text-sm font-medium text-burgundy mb-1">
                  Subcategory Name
                </label>
                <input
                  type="text"
                  value={newSubcategoryName}
                  onChange={(e) => setNewSubcategoryName(e.target.value)}
                  placeholder="e.g., hair/nails/skin, anxiety, libido"
                  className="w-full px-3 py-2 border border-burgundy/30 rounded-md focus:outline-none focus:ring-2 focus:ring-burgundy"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-burgundy mb-1">
                  Image (Optional)
                </label>
                {newSubcategoryImage ? (
                  <div className="relative">
                    <img src={newSubcategoryImage} alt="Selected" className="w-full h-32 object-cover rounded-md" />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setNewSubcategoryImage('')}
                      className="absolute top-2 right-2 bg-white"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowImageLibrary(true)}
                    className="w-full border-burgundy/30 text-burgundy"
                  >
                    <ImageIcon className="h-4 w-4 mr-2" />
                    Select Image
                  </Button>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-burgundy mb-1">
                  Order (for sorting)
                </label>
                <input
                  type="number"
                  value={newSubcategoryOrder}
                  onChange={(e) => setNewSubcategoryOrder(parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-burgundy/30 rounded-md focus:outline-none focus:ring-2 focus:ring-burgundy"
                />
              </div>
              <div className="flex items-center gap-2 p-3 bg-gold/10 rounded-lg border border-gold/30">
                <input
                  type="checkbox"
                  id="memberOnly"
                  checked={newSubcategoryMemberOnly}
                  onChange={(e) => setNewSubcategoryMemberOnly(e.target.checked)}
                  className="h-4 w-4 text-burgundy focus:ring-burgundy border-burgundy/30 rounded"
                />
                <label htmlFor="memberOnly" className="text-sm font-medium text-burgundy cursor-pointer">
                  🔒 Members Only - Lock entire subcategory for trial users
                </label>
              </div>
              <div className="flex gap-2">
                <Button
                  type="submit"
                  className="flex-1 bg-burgundy hover:bg-burgundy/80 text-cream"
                >
                  Create Subcategory
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowForm(false);
                    setNewSubcategoryName('');
                    setNewSubcategoryOrder(0);
                  }}
                  className="border-burgundy/30 text-burgundy"
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}

          {/* Subcategories List */}
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-burgundy"></div>
            </div>
          ) : subcategories.length === 0 ? (
            <div className="text-center py-8 text-burgundy/60">
              <List className="h-12 w-12 mx-auto mb-3 text-burgundy/30" />
              <p>No subcategories yet. Add one to organize your content!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {subcategories.map((sub) => (
                <div
                  key={sub.id}
                  className="flex items-center justify-between p-4 bg-white border border-burgundy/20 rounded-lg hover:shadow-md transition-shadow"
                >
                  {editingSubcategory?.id === sub.id ? (
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editingSubcategory.name}
                          onChange={(e) => setEditingSubcategory({...editingSubcategory, name: e.target.value})}
                          className="flex-1 px-2 py-1 border border-burgundy/30 rounded"
                          placeholder="Name"
                        />
                        <input
                          type="number"
                          value={editingSubcategory.order}
                          onChange={(e) => setEditingSubcategory({...editingSubcategory, order: parseInt(e.target.value) || 0})}
                          className="w-20 px-2 py-1 border border-burgundy/30 rounded"
                          placeholder="Order"
                        />
                      </div>
                      
                      <div className="flex items-center gap-2 p-2 bg-gold/10 rounded border border-gold/30">
                        <input
                          type="checkbox"
                          id={`memberOnly-${sub.id}`}
                          checked={editingSubcategory.member_only || false}
                          onChange={(e) => setEditingSubcategory({...editingSubcategory, member_only: e.target.checked})}
                          className="h-4 w-4 text-burgundy focus:ring-burgundy border-burgundy/30 rounded"
                        />
                        <label htmlFor={`memberOnly-${sub.id}`} className="text-xs font-medium text-burgundy cursor-pointer">
                          🔒 Members Only
                        </label>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {editingSubcategory.image_url && (
                          <div className="relative">
                            <img src={editingSubcategory.image_url} alt="Current" className="h-16 w-24 object-cover rounded" />
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditingSubcategory({...editingSubcategory, image_url: ''})}
                              className="absolute -top-2 -right-2 h-5 w-5 p-0 bg-white rounded-full border border-burgundy/30"
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setShowImageLibrary(true);
                          }}
                          className="border-burgundy/30 text-burgundy text-xs"
                        >
                          <ImageIcon className="h-3 w-3 mr-1" />
                          {editingSubcategory.image_url ? 'Change Image' : 'Add Image'}
                        </Button>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => {
                            updateSubcategory(sub.id, {
                              name: editingSubcategory.name, 
                              order: editingSubcategory.order,
                              image_url: editingSubcategory.image_url || null,
                              member_only: editingSubcategory.member_only || false
                            });
                          }}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingSubcategory(null)}
                          className="border-burgundy/30"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-3">
                        {sub.image_url && (
                          <img src={sub.image_url} alt={sub.name} className="h-12 w-16 object-cover rounded" />
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-burgundy">{sub.name}</p>
                            {sub.member_only && (
                              <span className="px-2 py-0.5 bg-gold/20 text-gold text-xs font-semibold rounded flex items-center gap-1">
                                🔒 Members Only
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-burgundy/60">Order: {sub.order}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingSubcategory(sub)}
                          className="border-burgundy/30 text-burgundy"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteSubcategory(sub.id)}
                          className="border-red-300 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Image Library Modal */}
          {showImageLibrary && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-cream border-burgundy">
                <CardHeader className="border-b border-burgundy/20">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-burgundy">Select Image</CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowImageLibrary(false)}
                      className="text-burgundy"
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="mb-4">
                    <input
                      type="text"
                      placeholder="Search images..."
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          searchImageLibrary(e.target.value || 'wellness');
                        }
                      }}
                      className="w-full px-4 py-2 border border-burgundy/30 rounded-md"
                    />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    {imageLibraryImages.length > 0 ? (
                      imageLibraryImages.map((img) => (
                        <div
                          key={img.id}
                          className="cursor-pointer hover:scale-105 transition-all border-2 border-transparent hover:border-burgundy rounded-lg overflow-hidden"
                          onClick={() => {
                            if (editingSubcategory) {
                              setEditingSubcategory({...editingSubcategory, image_url: img.urls.regular});
                            } else {
                              setNewSubcategoryImage(img.urls.regular);
                            }
                            setShowImageLibrary(false);
                            toast.success('Image selected!');
                          }}
                        >
                          <img
                            src={img.urls.small}
                            alt={img.alt_description || 'Image'}
                            className="w-full h-40 object-cover"
                          />
                        </div>
                      ))
                    ) : (
                      <div className="col-span-3 text-center py-8">
                        <p className="text-burgundy/60">Search for images above</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </CardContent>
      </Card>
    </div>,
    document.body
  );
};

export default SubcategoryManager;
